import { SettingItem } from '@/components/SettingItem/SettingItem';
import { useTranslation } from '@/i18n';
import {
  ButtonGroup,
  ButtonV2,
  Form,
  FormRadioButton,
  Heading,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useMemo } from 'react';
import styles from './PaacSetting.module.scss';
import {
  PAAC_LINKABLE_STATUS,
  PAAC_TAX_MODE,
  usePaacSetting,
  type PaacDetailFormValues,
} from './hooks/usePaacSetting';

const cx = classnames.bind(styles);

export const PaacSetting: FC = memo(() => {
  const { t } = useTranslation();
  const {
    isEnabled,
    isEditingDetail,
    taxMode,
    linkableStatus,
    formMethods,
    startUsing,
    stopUsing,
    startEditingDetail,
    cancelEditingDetail,
    saveDetail,
  } = usePaacSetting();

  const taxOptions = useMemo(
    () => [
      {
        id: PAAC_TAX_MODE.TAX_EXCLUSIVE,
        value: PAAC_TAX_MODE.TAX_EXCLUSIVE,
        label: t('paac_setting_tax_exclusive'),
      },
      {
        id: PAAC_TAX_MODE.TAX_INCLUSIVE,
        value: PAAC_TAX_MODE.TAX_INCLUSIVE,
        label: t('paac_setting_tax_inclusive'),
      },
    ],
    [t]
  );

  const linkableStatusOptions = useMemo(
    () => [
      {
        id: PAAC_LINKABLE_STATUS.APPROVED_ONLY,
        value: PAAC_LINKABLE_STATUS.APPROVED_ONLY,
        label: t('paac_setting_linkable_status_approved'),
      },
      {
        id: PAAC_LINKABLE_STATUS.APPROVED_AND_PENDING,
        value: PAAC_LINKABLE_STATUS.APPROVED_AND_PENDING,
        label: t('paac_setting_linkable_status_approved_and_pending'),
      },
    ],
    [t]
  );

  return (
    <div className={cx('container')}>
      <div className={cx('groups')}>
        <div className={cx('sections')}>
          <section className={cx('section')}>
            <div className={cx('section-header')}>
              <Heading as='h2' className={cx('title')}>
                {t('paac_setting_title')}
              </Heading>
              {isEnabled ? (
                <ButtonV2
                  size='sm'
                  isTertiary
                  type='button'
                  onClick={stopUsing}
                  isDisabled={isEditingDetail}
                >
                  {t('paac_setting_stop_button')}
                </ButtonV2>
              ) : (
                <ButtonV2 size='sm' type='button' onClick={startUsing}>
                  {t('paac_setting_start_button')}
                </ButtonV2>
              )}
            </div>
            <div className={cx('setting-items')}>
              <SettingItem
                label={t('paac_setting_usage_label')}
                value={t(
                  isEnabled
                    ? 'paac_setting_usage_enabled'
                    : 'paac_setting_usage_disabled'
                )}
              />
            </div>
          </section>
          {isEnabled && (
            <section className={cx('section')}>
              <div className={cx('section-header')}>
                <Heading as='h3' className={cx('sub-title')}>
                  {t('paac_setting_detail_title')}
                </Heading>
                {isEditingDetail ? (
                  <ButtonGroup direction='horizontal'>
                    <ButtonV2
                      size='sm'
                      isSecondary
                      type='button'
                      onClick={cancelEditingDetail}
                    >
                      {t('cancel')}
                    </ButtonV2>
                    <ButtonV2
                      size='sm'
                      type='button'
                      onClick={formMethods.handleSubmit(saveDetail)}
                    >
                      {t('save')}
                    </ButtonV2>
                  </ButtonGroup>
                ) : (
                  <ButtonV2
                    size='sm'
                    isSecondary
                    type='button'
                    onClick={startEditingDetail}
                  >
                    {t('paac_setting_edit_button')}
                  </ButtonV2>
                )}
              </div>
              <div className={cx('setting-items')}>
                {isEditingDetail ? (
                  <Form<PaacDetailFormValues>
                    formMethod={formMethods}
                    onSubmit={saveDetail}
                    name='paac_setting_detail'
                  >
                    <SettingItem
                      label={t('paac_setting_tax_label')}
                      value={
                        <FormRadioButton<PaacDetailFormValues>
                          options={taxOptions}
                          control={formMethods.control}
                          name='taxMode'
                          vertical
                          inputId='paac_setting_tax'
                          testId='paac_setting_tax'
                        />
                      }
                    />
                    <SettingItem
                      label={t('paac_setting_linkable_status_label')}
                      value={
                        <FormRadioButton<PaacDetailFormValues>
                          options={linkableStatusOptions}
                          control={formMethods.control}
                          name='linkableStatus'
                          vertical
                          inputId='paac_setting_linkable_status'
                          testId='paac_setting_linkable_status'
                        />
                      }
                    />
                  </Form>
                ) : (
                  <>
                    <SettingItem
                      label={t('paac_setting_tax_label')}
                      value={t(
                        taxMode === PAAC_TAX_MODE.TAX_EXCLUSIVE
                          ? 'paac_setting_tax_exclusive'
                          : 'paac_setting_tax_inclusive'
                      )}
                    />
                    <SettingItem
                      label={t('paac_setting_linkable_status_label')}
                      value={t(
                        linkableStatus === PAAC_LINKABLE_STATUS.APPROVED_ONLY
                          ? 'paac_setting_linkable_status_approved'
                          : 'paac_setting_linkable_status_approved_and_pending'
                      )}
                    />
                  </>
                )}
              </div>
            </section>
          )}
        </div>
        {isEnabled && (
          <section className={cx('section')}>
            <div className={cx('section-header')}>
              <Heading as='h2' className={cx('title')}>
                {t('paac_setting_status_page_title')}
              </Heading>
              <ButtonV2
                size='sm'
                isSecondary
                type='button'
                isDisabled={isEditingDetail}
              >
                {t('paac_setting_edit_button')}
              </ButtonV2>
            </div>
            <div className={cx('setting-items')}>
              <SettingItem
                label={t('paac_setting_display_label')}
                value={
                  <>
                    <div>{t('paac_setting_display_on')}</div>
                    <div className={cx('description')}>
                      {t('paac_setting_display_description')}
                    </div>
                  </>
                }
              />
            </div>
          </section>
        )}
      </div>
    </div>
  );
});
PaacSetting.displayName = 'PaacSetting';
